import { useTranslation } from 'react-i18next';
import { Establishment } from '@haccp/api-client';
import { formatAdminDate } from '../utils/admin';

interface EstablishmentWithDetails extends Establishment {
  owner?: { email: string };
  subscription?: {
    status: string;
    currentPeriodEnd?: Date;
  };
}

interface EstablishmentListProps {
  establishments: EstablishmentWithDetails[];
  isLoading?: boolean;
}

export default function EstablishmentList({
  establishments,
  isLoading = false,
}: EstablishmentListProps) {
  const { t } = useTranslation();

  const getSubscriptionStatus = (subscription?: { status: string }) => {
    if (!subscription) return { label: t('adminList.subNone'), color: 'bg-neutral-100 text-neutral-600' };
    const statuses: Record<string, { label: string; color: string }> = {
      ACTIVE: { label: t('adminList.subActive'), color: 'bg-green-100 text-green-800' },
      TRIALING: { label: t('adminList.subTrial'), color: 'bg-blue-100 text-blue-800' },
      PAST_DUE: { label: t('adminList.subPastDue'), color: 'bg-red-100 text-red-800' },
      CANCELED: { label: t('adminList.subCanceled'), color: 'bg-neutral-100 text-neutral-600' },
      LOCKED_MANUAL: { label: t('adminList.subLocked'), color: 'bg-orange-100 text-orange-800' },
    };
    return statuses[subscription.status] || { label: subscription.status, color: 'bg-neutral-100 text-neutral-600' };
  };

  const getPaymentStatus = (subscription?: { status: string; currentPeriodEnd?: Date }) => {
    if (!subscription) return { label: 'N/A', color: 'bg-neutral-100 text-neutral-500' };
    if (subscription.status === 'PAST_DUE') return { label: t('adminList.payUnpaid'), color: 'bg-red-100 text-red-800' };
    if (subscription.status === 'ACTIVE') return { label: t('adminList.payUpToDate'), color: 'bg-green-100 text-green-800' };
    if (subscription.status === 'TRIALING') return { label: t('adminList.payFree'), color: 'bg-blue-100 text-blue-800' };
    if (subscription.status === 'CANCELED') return { label: t('adminList.payTerminated'), color: 'bg-neutral-100 text-neutral-600' };
    return { label: '-', color: 'bg-neutral-100 text-neutral-500' };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-neutral-600">{t('adminList.loadingEstablishments')}</div>
      </div>
    );
  }

  if (establishments.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-lg font-medium text-neutral-900">{t('adminList.noEstablishments')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 bg-neutral-50">
            <th className="text-left px-4 py-3 font-semibold text-neutral-700">{t('adminList.colName')}</th>
            <th className="text-left px-4 py-3 font-semibold text-neutral-700">{t('adminList.colOwner')}</th>
            <th className="text-left px-4 py-3 font-semibold text-neutral-700">{t('adminList.colSubscription')}</th>
            <th className="text-left px-4 py-3 font-semibold text-neutral-700">{t('adminList.colPayment')}</th>
            <th className="text-left px-4 py-3 font-semibold text-neutral-700">{t('adminList.colCreated')}</th>
          </tr>
        </thead>
        <tbody>
          {establishments.map((est) => {
            const subStatus = getSubscriptionStatus(est.subscription);
            const payStatus = getPaymentStatus(est.subscription);
            return (
              <tr key={est.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                <td className="px-4 py-3 font-medium text-neutral-900">{est.name}</td>
                <td className="px-4 py-3 text-neutral-600">
                  {est.owner?.email || (
                    <span className="text-neutral-400 italic">{t('adminList.noOwner')}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${subStatus.color}`}>
                    {subStatus.label}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${payStatus.color}`}>
                    {payStatus.label}
                  </span>
                </td>
                <td className="px-4 py-3 text-neutral-600">{formatAdminDate(est.createdAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
